/**
 * useVideoStallRecovery.ts
 *
 * Composable que torna o <video> nativo resiliente a travamentos de rede.
 * O download progressivo do Chrome não tenta reconectar quando a conexão
 * com a origem (S3) cai no meio da reprodução: o player dispara
 * `waiting`/`stalled` e fica parado para sempre. Este composable detecta o
 * stall, distingue rede lenta (buffer ainda cresce → só espera) de download
 * morto (buffer congelado → força reload do elemento e retoma do ponto).
 *
 * Uso em qualquer componente Vue:
 *
 *   import { useVideoStallRecovery } from '@/composables/useVideoStallRecovery'
 *
 *   const stall = useVideoStallRecovery(() => { falhaDefinitiva() })
 *
 *   <video @waiting="stall.onStall" @stalled="stall.onStall"
 *          @playing="stall.onPlaying" @error="stall.onError" ... >
 */
import { onBeforeUnmount } from "vue";

/** Tempo parado antes de avaliar se o download morreu. */
const STALL_TIMEOUT_MS = 5000;
/** Máximo de recuperações consecutivas sem progresso antes de parar de tentar. */
const MAX_CONSECUTIVE_RECOVERIES = 3;

export function useVideoStallRecovery(onGiveUp?: () => void) {
  let stallTimer: number | null = null;
  let recoveries = 0;
  let pendingResume: (() => void) | null = null;
  let pendingResumeTarget: HTMLVideoElement | null = null;

  function clearStallTimer() {
    if (stallTimer !== null) {
      clearTimeout(stallTimer);
      stallTimer = null;
    }
  }

  /** Fim do trecho bufferizado — proxy de "o download ainda progride?". */
  function bufferedEnd(video: HTMLVideoElement): number {
    try {
      return video.buffered.length ? video.buffered.end(video.buffered.length - 1) : 0;
    } catch {
      return 0;
    }
  }

  function recover(video: HTMLVideoElement) {
    // Retentativas esgotadas: para em silêncio. O player nativo continua na
    // tela com posição e controles — trocar por caixa de erro seria pior.
    if (recoveries >= MAX_CONSECUTIVE_RECOVERIES) return;
    recoveries++;
    const resumeAt = video.currentTime;
    const wasPlaying = !video.paused && !video.ended;
    const srcAtRecovery = video.currentSrc || video.src;
    // Remove retomada pendente de uma recuperação anterior que nunca carregou —
    // senão ela dispararia num load futuro (ex.: troca de vídeo na galeria).
    if (pendingResume && pendingResumeTarget) {
      pendingResumeTarget.removeEventListener("loadedmetadata", pendingResume);
    }
    pendingResume = () => {
      pendingResume = null;
      pendingResumeTarget = null;
      // src mudou entre o reload e o load: usuário navegou para outro vídeo.
      if ((video.currentSrc || video.src) !== srcAtRecovery) return;
      video.currentTime = resumeAt;
      if (wasPlaying) void video.play().catch(() => undefined);
    };
    pendingResumeTarget = video;
    video.addEventListener("loadedmetadata", pendingResume, { once: true });
    // load() refaz a request do src; com suporte a Range (S3) o navegador
    // retoma o download a partir do ponto do seek, sem rebaixar tudo.
    video.load();
  }

  function armStallTimer(video: HTMLVideoElement) {
    clearStallTimer();
    const bufferedAtArm = bufferedEnd(video);
    stallTimer = window.setTimeout(() => {
      stallTimer = null;
      if (video.paused || video.ended || video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return;
      // Buffer cresceu no intervalo → rede lenta mas viva: reload só atrapalharia.
      if (bufferedEnd(video) > bufferedAtArm) {
        armStallTimer(video);
        return;
      }
      recover(video);
    }, STALL_TIMEOUT_MS);
  }

  /** Ligar em @waiting e @stalled. */
  function onStall(event: Event) {
    const video = event.target as HTMLVideoElement;
    if (video.paused || video.ended) return;
    armStallTimer(video);
  }

  /** Ligar em @playing — reprodução retomou, zera o estado de falha. */
  function onPlaying() {
    clearStallTimer();
    recoveries = 0;
  }

  /** Ligar em @error — erro de rede tenta recuperar; src inválido desiste na hora. */
  function onError(event: Event) {
    const video = event.target as HTMLVideoElement;
    clearStallTimer();
    // Sem src ou formato/URL inválido (404, codec): irrecuperável.
    if (!video.error || video.error.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
      onGiveUp?.();
      return;
    }
    recover(video);
  }

  onBeforeUnmount(clearStallTimer);

  return { onStall, onPlaying, onError };
}
